import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { userAPI, projectAPI, threadAPI, authAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { 
  Github, Linkedin, Globe, MapPin, Calendar, Star, Award, 
  FolderOpen, MessageSquare, Users, Loader2, UserPlus, UserMinus
} from 'lucide-react';

export default function MemberProfile() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [threads, setThreads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [profileRes, currentUserRes] = await Promise.all([
        userAPI.getProfile(userId),
        authAPI.getMe().catch(() => null)
      ]);
      
      setProfile(profileRes.data);
      if (currentUserRes) {
        setCurrentUser(currentUserRes.data);
        setIsFollowing(currentUserRes.data.following?.includes(userId));
      }

      // Fetch user's content
      const [projectsRes, threadsRes] = await Promise.all([
        projectAPI.getAll({ limit: 100 }),
        threadAPI.getAll({ author_id: userId, limit: 100 })
      ]);
      
      setProjects(projectsRes.data.filter(p => p.author_id === userId));
      setThreads(threadsRes.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!currentUser) {
      toast.error('Please sign in to follow users');
      return;
    }

    try {
      const response = await userAPI.followUser(userId);
      setIsFollowing(response.data.following);
      setProfile(prev => ({
        ...prev,
        stats: {
          ...prev.stats,
          followers: prev.stats.followers + (response.data.following ? 1 : -1)
        }
      }));
      toast.success(response.data.following ? 'Following!' : 'Unfollowed');
    } catch (error) {
      toast.error('Failed to follow user');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-slate-600">User not found</p>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser?.user_id === userId;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="member-profile-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <Card className="mb-8">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <Avatar className="w-24 h-24">
                <AvatarImage src={profile.picture} />
                <AvatarFallback className="text-3xl bg-indigo-100 text-indigo-700">
                  {profile.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h1 className="text-2xl font-bold text-slate-900">{profile.name}</h1>
                      {profile.role === 'mentor' && (
                        <Badge className="bg-purple-100 text-purple-700">Mentor</Badge>
                      )}
                      {profile.role === 'admin' && (
                        <Badge className="bg-red-100 text-red-700">Admin</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="secondary"
                        className={`bg-${profile.level?.color}-100 text-${profile.level?.color}-700`}
                      >
                        <Star className="w-3 h-3 mr-1" />
                        {profile.level?.name} • {profile.reputation} pts
                      </Badge>
                    </div>
                  </div>

                  {!isOwnProfile && (
                    <div className="flex gap-2">
                      <Button
                        variant={isFollowing ? "outline" : "default"}
                        onClick={handleFollow}
                        className={isFollowing ? "" : "bg-indigo-600"}
                      >
                        {isFollowing ? (
                          <><UserMinus className="w-4 h-4 mr-2" />Unfollow</>
                        ) : (
                          <><UserPlus className="w-4 h-4 mr-2" />Follow</>
                        )}
                      </Button>
                      <Link to={`/messages/${userId}`}>
                        <Button variant="outline">Message</Button>
                      </Link>
                    </div>
                  )}
                </div>

                {profile.bio && (
                  <p className="text-slate-600 mb-4">{profile.bio}</p>
                )}

                {/* Stats */}
                <div className="flex flex-wrap gap-6 text-sm text-slate-600 mb-4">
                  <div className="flex items-center gap-1">
                    <FolderOpen className="w-4 h-4" />
                    <span><strong>{profile.stats?.projects || 0}</strong> projects</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" />
                    <span><strong>{profile.stats?.posts || 0}</strong> posts</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span><strong>{profile.stats?.followers || 0}</strong> followers</span>
                  </div>
                </div>

                {/* Info */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                  {profile.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {profile.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Joined {profile.created_at ? formatDistanceToNow(new Date(profile.created_at), { addSuffix: true }) : 'recently'}
                  </span>
                </div>

                {/* Social Links */}
                <div className="flex items-center gap-3 mt-4">
                  {profile.github_url && (
                    <a href={profile.github_url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-900">
                      <Github className="w-5 h-5" />
                    </a>
                  )}
                  {profile.linkedin_url && (
                    <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-blue-600">
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                  {profile.website_url && (
                    <a href={profile.website_url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-indigo-600">
                      <Globe className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Badges */}
            {profile.badges?.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <h3 className="text-sm font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Badges
                </h3>
                <div className="flex flex-wrap gap-2">
                  {profile.badges.map((badge) => (
                    <Badge key={badge} variant="secondary">
                      {badge.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Skills */}
            {profile.skills?.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-slate-900 mb-2">Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((skill) => (
                    <Badge key={skill} variant="outline">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Content Tabs */}
        <Tabs defaultValue="projects">
          <TabsList className="mb-6">
            <TabsTrigger value="projects" className="gap-2">
              <FolderOpen className="w-4 h-4" />
              Projects ({projects.length})
            </TabsTrigger>
            <TabsTrigger value="threads" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              Posts ({threads.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
            {projects.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No projects yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {projects.map((project) => (
                  <Link key={project.project_id} to={`/projects/${project.project_id}`}>
                    <Card className="card-hover h-full">
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-slate-900 mb-2">{project.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3">{project.description}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>↑ {project.upvotes - project.downvotes}</span>
                          <span>{project.comment_count} comments</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="threads">
            {threads.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No posts yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {threads.map((thread) => (
                  <Link key={thread.thread_id} to={`/forum/thread/${thread.thread_id}`}>
                    <Card className="card-hover">
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-slate-900 mb-2">{thread.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3">{thread.content}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>↑ {thread.upvotes - thread.downvotes}</span>
                          <span>{thread.reply_count} replies</span>
                          <span>{formatDistanceToNow(new Date(thread.created_at), { addSuffix: true })}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
